"use client";

import { type ComponentProps } from "react";

import { env } from "@/env";
import { cn } from "@/lib/utils";
import makeSlug from "@/utils/makeSlug";
import Link from "@app/_components/Link";
import useTranslate from "@app/_hooks/useTranslate";
import { Market } from "@features/market/config/i18n-config";
import { ArrowLeftIcon } from "lucide-react";
import Script from "next/script";
import { Breadcrumb } from "./getPageBreadcrumbs";

function crumbHref(
  item: Breadcrumb,
  index: number,
  items: Breadcrumb[],
  basePath: string,
) {
  const prefix =
    index === items.length - 1 && basePath && basePath !== item.slug
      ? basePath
      : undefined;
  return makeSlug(prefix, item.slug);
}

function SEO({
  items,
  market,
  basePath,
}: {
  items: Breadcrumb[];
  market: Market;
  basePath: string;
}) {
  if (!items.length) return null;

  return (
    <Script
      id="structured-data-breadcrumbs-list"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(
          {
            "@context": "https://schema.org/",
            "@type": "BreadcrumbList",
            itemListElement: items.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              item:
                env.NEXT_PUBLIC_APP_BASE_URL +
                makeSlug(
                  market.siteLocale,
                  crumbHref(item, index, items, basePath),
                ),
            })),
          },
          null,
          2,
        ),
      }}
    />
  );
}

function Page({
  href,
  market,
  children,
  className,
}: ComponentProps<"span"> & {
  href: string;
  market: Market;
}) {
  return (
    <Link
      href={href}
      locale={market.siteLocale}
      className={cn(
        "inline-flex content-center items-center px-2 py-1",
        className,
      )}
    >
      {children}
    </Link>
  );
}

type ProductListingBreadcrumbsProps = ComponentProps<"nav"> & {
  items: Breadcrumb[];
  market: Market;
  basePath?: string;
};

export default function Breadcrumbs({
  items,
  market,
  basePath = "",
  className,
  ...props
}: ProductListingBreadcrumbsProps) {
  const t = useTranslate();
  if (items.length <= 1) return null;
  const backIndex = items.length - 2;
  const backItem = items.at(backIndex);
  return (
    <>
      <SEO items={items} market={market} basePath={basePath} />
      <nav
        aria-label="Breadcrumb"
        className={cn(
          "scrollbar-none flex items-center gap-3 overflow-x-auto overflow-y-hidden scroll-smooth text-sm text-gray-300 uppercase",
          className,
        )}
        {...props}
      >
        {backItem ? (
          <Page
            href={crumbHref(backItem, backIndex, items, basePath)}
            market={market}
            className="border-gray-150 gap-x-2 border bg-white text-black hover:border-black"
          >
            <ArrowLeftIcon
              strokeWidth={1}
              className="size-4 max-lg:text-black"
            />
            {t("back")}
          </Page>
        ) : null}
        <ol className="flex items-center">
          {items.map((item, index) => (
            <li key={item.slug}>
              {index < items.length - 1 ? (
                <>
                  <Page
                    href={crumbHref(item, index, items, basePath)}
                    market={market}
                  >
                    {item.title}
                  </Page>
                  <span aria-hidden>/</span>
                </>
              ) : (
                <span className="px-2 py-1 whitespace-nowrap text-black">
                  {item.title}
                </span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}
